import { useEffect, useMemo, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { BrandFavicon } from '../BrandFavicon';
import { useProject } from '../../context/ProjectContext';
import { buildDateRangeQueryOptions } from '../../utils/visibility-range';
import { formatSourceDisplayType, sourceDisplayLabel } from '../../utils/source-insights';
import type { SourceFilterOptionItem } from '../../types';

export function SourceFilter() {
  const {
    projectId,
    topicFilterId,
    modelFilterId,
    visibilityRange,
    customFrom,
    customTo,
    sourceFilterIds,
    toggleSourceFilterId,
    clearSourceFilterIds,
  } = useProject();
  const [options, setOptions] = useState<SourceFilterOptionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const rangeQuery = useMemo(
    () =>
      buildDateRangeQueryOptions(visibilityRange, customFrom, customTo, {
        topicId: topicFilterId ?? undefined,
        providerIds: modelFilterId ? [modelFilterId] : undefined,
      }),
    [visibilityRange, customFrom, customTo, topicFilterId, modelFilterId],
  );

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    api
      .getSourceFilterOptions(projectId, rangeQuery)
      .then((result) => {
        if (!cancelled) setOptions(result.items);
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, rangeQuery]);

  const label = useMemo(() => {
    if (sourceFilterIds.length === 0) return 'همه منابع';
    if (sourceFilterIds.length === 1) {
      const selected = options.find((item) => item.id === sourceFilterIds[0]);
      return selected ? sourceDisplayLabel(selected.host, selected.displayName) : '۱ منبع';
    }
    return `${sourceFilterIds.length} منبع`;
  }, [sourceFilterIds, options]);

  const isActive = sourceFilterIds.length > 0;

  return (
    <Dropdown show={open} onToggle={(next) => setOpen(next)}>
      <Dropdown.Toggle
        as="button"
        className={classNames(
          'btn d-inline-flex align-items-center gap-1 rounded-pill border px-2 py-1 fs--2 fw-medium lh-sm',
          isActive || open
            ? 'bg-body text-1000 border-400 shadow-sm'
            : 'bg-body-tertiary text-700 border-300',
        )}
      >
        <span className="text-500 d-inline-flex">
          <FontAwesomeIcon icon="link" className="fs--2" />
        </span>
        <span className="text-truncate" style={{ maxWidth: '9rem' }}>
          {label}
        </span>
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: 260, maxHeight: 320, overflowY: 'auto' }} className="py-1">
        <Dropdown.Item
          onClick={() => clearSourceFilterIds()}
          active={sourceFilterIds.length === 0}
          className="fs--1"
        >
          همه منابع
        </Dropdown.Item>
        <Dropdown.Divider />
        {loading && <div className="px-3 py-2 fs--2 text-500">در حال بارگذاری…</div>}
        {!loading && options.length === 0 && (
          <div className="px-3 py-2 fs--2 text-500">منبعی یافت نشد</div>
        )}
        {options.map((option) => {
          const selected = sourceFilterIds.includes(option.id);
          return (
            <Dropdown.Item
              key={option.id}
              onClick={() => toggleSourceFilterId(option.id)}
              active={selected}
              className="d-flex align-items-center gap-2 fs--1"
            >
              <Form.Check type="checkbox" readOnly checked={selected} className="mb-0" />
              <BrandFavicon
                host={option.host}
                name={sourceDisplayLabel(option.host, option.displayName)}
                size="xs"
              />
              <span className="flex-grow-1 text-truncate">
                {sourceDisplayLabel(option.host, option.displayName)}
              </span>
              <span className="fs--2 text-500">
                {formatSourceDisplayType(option.displayType)}
              </span>
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
